import { z } from 'zod'
import './registry'
import { IsoDateSchema } from './common'

/**
 * Cached staff display names (Fase 4 transactions report). `Transaction.cashierId`
 * / `.reversedBy` are raw Cognito subs — not human-readable, and Cognito itself
 * has no name for anyone today (the invite flow only ever sets `email`; there's
 * no `given_name`/`family_name` attribute in play). This cache is the resolution
 * path: resolve a sub → Cognito (email only) on first sight, remember it here,
 * and let an admin type in a real `displayName` once. Because it's a durable
 * cache rather than a live lookup, a name (or an admin's override) survives even
 * after that Cognito user is later deleted (staff turnover) — `email` is the
 * best-effort fallback, `displayName` always wins when set.
 */
export const StaffProfileSchema = z
  .object({
    sub: z.string().min(1),
    /** Cached from Cognito at first resolution; null if never found there. */
    email: z.string().nullable(),
    /** Admin-set friendly name; takes priority over `email` for display. */
    displayName: z.string().nullable(),
    updatedAt: IsoDateSchema,
  })
  .openapi('StaffProfile')
export type StaffProfile = z.infer<typeof StaffProfileSchema>

export const RenameStaffRequestSchema = z
  .object({
    displayName: z.string().trim().min(1).max(100),
  })
  .strict()
  .openapi('RenameStaffRequest')
export type RenameStaffRequest = z.infer<typeof RenameStaffRequestSchema>
